import { type Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useExecutionRunsTableToolbarState } from '@/features/executionRuns/pages/executionRunsTableToolbarState';
import {
  buildChartDates,
  buildLineChartSeries,
  buildMiniMetricLineChartSeries,
} from '@/features/executions/utils/chartData';
import { buildStackedExecutionChartData } from '@/features/executions/utils/stackedExecutionChart';
import { useMetricsBatchQuery, useMetricsQuery } from '@/features/metrics/hooks/metrics';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { useQualityDateRange } from '@/features/quality/hooks';
import { collapseGroupedStackedChartData } from '@/features/shared/hooks/useEntityTrendsPage.statusPresentation';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildMiniMetricChartSummary } from './miniMetricChartSummary';
import type { MiniMetricChartSummary } from './MiniMetricChartHeader';
import type { MiniMetricLineChartDatum } from '@/features/executions/utils/chartData';
import type { ExecutionStatusKey, MetricsScope } from '@/features/metrics/metricsHelpers';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type { UiTone } from '@/theme/uiTokens';
import type { MetricThresholdGroup } from '@flowqa/shared';

type UseMetricsStatusChartPanelModelParams<TStatus extends string> = {
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  externalToolbarState?: MetricsStatusChartPanelModelToolbarState<TStatus>;
  disabled?: boolean;
};

export type MetricsStatusChartPanelModelToolbarState<TStatus extends string> = {
  search: string;
  setSearch: (value: string) => void;
  selectedStatuses: TStatus[];
  setSelectedStatuses: (value: TStatus[]) => void;
  selectedEntities?: string[];
  setSelectedEntities?: (value: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  selectedItems?: string[];
  setSelectedItems?: (value: string[]) => void;
  itemOptions?: TableDropdownOption[];
  itemLabel?: string;
  selectedSuites?: string[];
  setSelectedSuites?: (value: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  suiteLabel?: string;
  selectedTags: string[];
  setSelectedTags: (value: string[]) => void;
  selectedProjects?: string[];
  setSelectedProjects?: (value: string[]) => void;
  projectOptions?: TableDropdownOption[];
  noRowsText: string;
  dateQuickFilter: DateQuickFilter;
  onDateQuickFilterChange: (value: DateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (value: string | null) => void;
  onToDateChange: (value: string | null) => void;
  latestQueryStartDay?: string | null;
  latestQueryEndDay?: string | null;
};

export type MetricsStatusChartPanelDisplayModel = {
  statusChartData: {
    data: Array<Record<string, string | number>>;
    series: Array<{ key: string; label: string; color?: string; colorKey?: UiTone }>;
  } | null;
  passRateChartData: Array<Record<string, string | number | null>> | null;
  frequencyChartData: Array<Record<string, string | number | null>> | null;
  flakeScoreChartData?: Array<Record<string, string | number | null>> | null;
  durationChartData: Array<Record<string, string | number | null>> | null;
  passRateMiniChartData: MiniMetricLineChartDatum[] | null;
  frequencyMiniChartData: MiniMetricLineChartDatum[] | null;
  flakeScoreMiniChartData?: MiniMetricLineChartDatum[] | null;
  durationMiniChartData: MiniMetricLineChartDatum[] | null;
  showStatusChartSkeleton: boolean;
  showChartErrorState: boolean;
  passRateSummary: MiniMetricChartSummary | null;
  frequencySummary: MiniMetricChartSummary | null;
  flakeScoreSummary?: MiniMetricChartSummary | null;
  durationSummary: MiniMetricChartSummary | null;
  facetCounts: {
    statusCounts: Partial<Record<string, number>>;
    entityCounts: Partial<Record<string, number>>;
  };
};

type MetricsStatusChartPanelModel<TStatus extends string> = {
  toolbarState: MetricsStatusChartPanelModelToolbarState<TStatus>;
} & MetricsStatusChartPanelDisplayModel;

export const useMetricsStatusChartPanelModel = <TStatus extends string>({
  noRowsBaseText,
  scope,
  projectId,
  externalToolbarState,
  disabled = false,
}: UseMetricsStatusChartPanelModelParams<TStatus>): MetricsStatusChartPanelModel<TStatus> => {
  const internalToolbarState = useExecutionRunsTableToolbarState<TStatus>({
    dateField: 'startedAt',
    noRowsBaseText,
  });
  const baseToolbarState = (externalToolbarState ??
    internalToolbarState) as MetricsStatusChartPanelModelToolbarState<TStatus>;
  const selectedMetricEntityId = useMemo(() => {
    const selectedEntities = baseToolbarState.selectedEntities ?? [];
    if (selectedEntities.length !== 1) return null;
    return selectedEntities[0] ?? null;
  }, [baseToolbarState.selectedEntities]);
  const selectedMetricEntityIds = useMemo(() => {
    const selectedEntities = baseToolbarState.selectedEntities ?? [];
    return selectedEntities.length > 1 ? selectedEntities : [];
  }, [baseToolbarState.selectedEntities]);
  const selectedMetricSuiteIds = useMemo(
    () => (scope === 'test' ? (baseToolbarState.selectedSuites ?? []) : []),
    [baseToolbarState.selectedSuites, scope],
  );
  const dashboardDateRange = useQualityDateRange(baseToolbarState.dateQuickFilter);
  const { thresholds } = useProjectMetricThresholds(projectId);
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const metricThresholds = useMemo<MetricThresholdGroup>(() => {
    if (scope === 'suite') return thresholds.suites;
    if (scope === 'test') return thresholds.tests;
    if (scope === 'stage') return thresholds.stages;
    if (scope === 'job') return thresholds.jobs;
    return thresholds.executionRuns;
  }, [
    scope,
    thresholds.executionRuns,
    thresholds.jobs,
    thresholds.stages,
    thresholds.suites,
    thresholds.tests,
  ]);
  const hasCustomDateRange = baseToolbarState.fromDate !== null || baseToolbarState.toDate !== null;
  const isLatestOnlyMode =
    baseToolbarState.dateQuickFilter === 'latest' &&
    baseToolbarState.fromDate === null &&
    baseToolbarState.toDate === null;
  const previousMetricsWindow = useMemo(() => {
    if (!hasCustomDateRange) {
      return {
        startDay: dashboardDateRange.previousRangeStartDay,
        endDay: dashboardDateRange.previousRangeEndDay,
      };
    }
    const rawStart = (baseToolbarState.fromDate ?? baseToolbarState.toDate)?.startOf('day') ?? null;
    const rawEnd = (baseToolbarState.toDate ?? baseToolbarState.fromDate)?.startOf('day') ?? null;
    if (rawStart === null || rawEnd === null) return { startDay: null, endDay: null };
    const start = rawStart.isAfter(rawEnd) ? rawEnd : rawStart;
    const end = rawStart.isAfter(rawEnd) ? rawStart : rawEnd;
    const spanDays = end.diff(start, 'day') + 1;
    const previousEnd = start.subtract(1, 'day');
    const previousStart = previousEnd.subtract(spanDays - 1, 'day');
    return {
      startDay: previousStart.format('YYYY-MM-DD'),
      endDay: previousEnd.format('YYYY-MM-DD'),
    };
  }, [
    dashboardDateRange.previousRangeEndDay,
    dashboardDateRange.previousRangeStartDay,
    hasCustomDateRange,
    baseToolbarState.fromDate,
    baseToolbarState.toDate,
  ]);
  const showMetricDelta =
    scope !== 'pipeline' &&
    (hasCustomDateRange || baseToolbarState.dateQuickFilter !== 'latest') &&
    previousMetricsWindow.startDay !== null &&
    previousMetricsWindow.endDay !== null;
  const metricsRange =
    hasCustomDateRange || isLatestOnlyMode ? 'all' : baseToolbarState.dateQuickFilter;
  const metricsName = baseToolbarState.search.trim().length > 0 ? baseToolbarState.search : null;
  const metricsStartDay = hasCustomDateRange
    ? (baseToolbarState.fromDate?.format('YYYY-MM-DD') ?? null)
    : null;
  const metricsEndDay = hasCustomDateRange
    ? (baseToolbarState.toDate?.format('YYYY-MM-DD') ?? null)
    : null;
  const metricsQuery = useMetricsQuery({
    range: metricsRange,
    statuses: baseToolbarState.selectedStatuses as ExecutionStatusKey[],
    tags: baseToolbarState.selectedTags,
    suiteIds: selectedMetricSuiteIds,
    scope,
    projectId,
    entityId: selectedMetricEntityId,
    entityIds: selectedMetricEntityIds,
    name: metricsName,
    startDay: metricsStartDay,
    endDay: metricsEndDay,
    latestOnly: isLatestOnlyMode,
    enabled: !disabled,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const statusFacetQuery = useMetricsQuery({
    range: metricsRange,
    statuses: [],
    tags: baseToolbarState.selectedTags,
    suiteIds: selectedMetricSuiteIds,
    scope,
    projectId,
    entityId: selectedMetricEntityId,
    entityIds: selectedMetricEntityIds,
    name: metricsName,
    startDay: metricsStartDay,
    endDay: metricsEndDay,
    latestOnly: isLatestOnlyMode,
    enabled: !disabled,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const entityFacetOptions = baseToolbarState.entityOptions ?? [];
  const uncountedEntityFacetOptions = useMemo(
    () => entityFacetOptions.filter((option) => option.count === undefined),
    [entityFacetOptions],
  );
  const entityFacetQuery = useMetricsBatchQuery({
    range: metricsRange,
    statuses: baseToolbarState.selectedStatuses as ExecutionStatusKey[],
    tags: baseToolbarState.selectedTags,
    suiteIds: selectedMetricSuiteIds,
    scope,
    name: metricsName,
    startDay: metricsStartDay,
    endDay: metricsEndDay,
    latestOnly: isLatestOnlyMode,
    entities: uncountedEntityFacetOptions.map((option) => ({
      entityId: option.value,
      projectId: option.projectId ?? projectId,
    })),
    enabled: !disabled && uncountedEntityFacetOptions.length > 0,
    preservePreviousData: true,
  });
  const suiteFacetOptions = baseToolbarState.suiteOptions ?? [];
  const uncountedSuiteFacetOptions = useMemo(
    () => suiteFacetOptions.filter((option) => option.count === undefined),
    [suiteFacetOptions],
  );
  const suiteFacetQuery = useMetricsBatchQuery({
    range: metricsRange,
    statuses: baseToolbarState.selectedStatuses as ExecutionStatusKey[],
    tags: baseToolbarState.selectedTags,
    scope: 'suite',
    name: metricsName,
    startDay: metricsStartDay,
    endDay: metricsEndDay,
    latestOnly: isLatestOnlyMode,
    entities: uncountedSuiteFacetOptions.map((option) => ({
      entityId: option.value,
      projectId: option.projectId ?? projectId,
    })),
    enabled: !disabled && uncountedSuiteFacetOptions.length > 0,
    preservePreviousData: true,
  });
  const itemFacetOptions = baseToolbarState.itemOptions ?? [];
  const uncountedItemFacetOptions = useMemo(
    () => itemFacetOptions.filter((option) => option.count === undefined),
    [itemFacetOptions],
  );
  const itemFacetQuery = useMetricsBatchQuery({
    range: metricsRange,
    statuses: baseToolbarState.selectedStatuses as ExecutionStatusKey[],
    tags: baseToolbarState.selectedTags,
    suiteIds: selectedMetricSuiteIds,
    scope,
    name: metricsName,
    startDay: metricsStartDay,
    endDay: metricsEndDay,
    latestOnly: isLatestOnlyMode,
    entities: uncountedItemFacetOptions.map((option) => ({
      entityId: option.value,
      projectId: option.projectId ?? projectId,
    })),
    enabled: !disabled && uncountedItemFacetOptions.length > 0,
    preservePreviousData: true,
  });
  const previousMetricsQuery = useMetricsQuery({
    range: 'all',
    statuses: baseToolbarState.selectedStatuses as ExecutionStatusKey[],
    tags: baseToolbarState.selectedTags,
    suiteIds: selectedMetricSuiteIds,
    scope,
    projectId,
    entityId: selectedMetricEntityId,
    entityIds: selectedMetricEntityIds,
    name: metricsName,
    startDay: showMetricDelta ? previousMetricsWindow.startDay : null,
    endDay: showMetricDelta ? previousMetricsWindow.endDay : null,
    enabled: showMetricDelta && !disabled,
    preservePreviousData: true,
    refetchOnWindowFocus: false,
  });
  const metricsRangeDates = metricsQuery.data?.metricsRangeDates ?? [];
  const latestMetricStartDay = metricsRangeDates[0] ?? null;
  const latestMetricEndDay =
    metricsRangeDates.length > 0 ? (metricsRangeDates[metricsRangeDates.length - 1] ?? null) : null;
  const buildFacetCounts = (
    data: typeof entityFacetQuery.data,
  ): Partial<Record<string, number>> => {
    const entries = (data ?? []).map((item) => {
      const count = item.metrics.statusSummary.reduce(
        (sum, statusItem) => sum + statusItem.value,
        0,
      );
      return [item.entityId, count] as const;
    });
    return Object.fromEntries(entries) as Partial<Record<string, number>>;
  };
  const entityFacetCounts = useMemo(
    () => buildFacetCounts(entityFacetQuery.data),
    [entityFacetQuery.data],
  );
  const suiteFacetCounts = useMemo(
    () => buildFacetCounts(suiteFacetQuery.data),
    [suiteFacetQuery.data],
  );
  const itemFacetCounts = useMemo(
    () => buildFacetCounts(itemFacetQuery.data),
    [itemFacetQuery.data],
  );
  const statusFacetCounts = useMemo<Partial<Record<string, number>>>(
    () =>
      Object.fromEntries(
        (statusFacetQuery.data?.statusMetrics ?? []).map((statusItem) => [
          statusItem.key,
          statusItem.count,
        ]),
      ) as Partial<Record<string, number>>,
    [statusFacetQuery.data?.statusMetrics],
  );
  const countedEntityOptions = useMemo(
    () =>
      baseToolbarState.entityOptions?.map((option) => ({
        ...option,
        count: option.count ?? entityFacetCounts[option.value] ?? 0,
      })),
    [baseToolbarState.entityOptions, entityFacetCounts],
  );
  const countedSuiteOptions = useMemo(
    () =>
      baseToolbarState.suiteOptions?.map((option) => ({
        ...option,
        count: option.count ?? suiteFacetCounts[option.value] ?? 0,
      })),
    [baseToolbarState.suiteOptions, suiteFacetCounts],
  );
  const countedItemOptions = useMemo(
    () =>
      baseToolbarState.itemOptions?.map((option) => ({
        ...option,
        count: option.count ?? itemFacetCounts[option.value] ?? 0,
      })),
    [baseToolbarState.itemOptions, itemFacetCounts],
  );
  const toolbarState = useMemo<MetricsStatusChartPanelModelToolbarState<TStatus>>(
    () => ({
      ...baseToolbarState,
      entityOptions: countedEntityOptions,
      suiteOptions: countedSuiteOptions,
      itemOptions: countedItemOptions,
      latestQueryStartDay: baseToolbarState.latestQueryStartDay ?? latestMetricStartDay,
      latestQueryEndDay: baseToolbarState.latestQueryEndDay ?? latestMetricEndDay,
    }),
    [
      baseToolbarState,
      countedEntityOptions,
      countedItemOptions,
      countedSuiteOptions,
      latestMetricEndDay,
      latestMetricStartDay,
    ],
  );
  const statusChartData = useMemo(() => {
    const baseData = buildStackedExecutionChartData(metricsQuery.data);
    return groupErroredWithFailed ? collapseGroupedStackedChartData(baseData) : baseData;
  }, [groupErroredWithFailed, metricsQuery.data]);
  const measuredChartLabels = useMemo(() => {
    const labels = new Set<string>();
    statusChartData?.data.forEach((row) => {
      const hasStatusCount = statusChartData.series.some((seriesItem) => {
        const value = row[seriesItem.key];
        return typeof value === 'number' && Number.isFinite(value) && value > 0;
      });
      if (hasStatusCount) {
        labels.add(String(row.label));
      }
    });
    return labels;
  }, [statusChartData]);
  const summaryMetrics = measuredChartLabels.size > 0 ? (metricsQuery.data ?? null) : null;
  const chartDates = useMemo(() => {
    const dates = metricsQuery.data?.metricsRangeDates ?? [];
    return buildChartDates(dates, dates);
  }, [metricsQuery.data?.metricsRangeDates]);
  const lineChartLabels = metricsQuery.data?.metricsRangeLabels ?? [];
  const passRateChartData = useMemo(
    () =>
      buildLineChartSeries(
        metricsQuery.data?.series.filteredPassRate,
        lineChartLabels,
        chartDates,
        'passRate',
      ),
    [chartDates, lineChartLabels, metricsQuery.data?.series.filteredPassRate],
  );
  const frequencyChartData = useMemo(
    () =>
      buildLineChartSeries(
        metricsQuery.data?.series.executionTrendMetrics,
        lineChartLabels,
        chartDates,
        'frequency',
      ),
    [chartDates, lineChartLabels, metricsQuery.data?.series.executionTrendMetrics],
  );
  const durationChartData = useMemo(
    () =>
      buildLineChartSeries(
        metricsQuery.data?.series.executionDurationAverage,
        lineChartLabels,
        chartDates,
        'duration',
      ),
    [chartDates, lineChartLabels, metricsQuery.data?.series.executionDurationAverage],
  );
  const flakeScoreChartData = useMemo(
    () =>
      buildLineChartSeries(
        metricsQuery.data?.series.flakeScoreTrend,
        lineChartLabels,
        chartDates,
        'flakeScore',
      ),
    [chartDates, lineChartLabels, metricsQuery.data?.series.flakeScoreTrend],
  );
  const passRateMiniChartData = useMemo(
    () =>
      buildMiniMetricLineChartSeries(
        metricsQuery.data?.series.filteredPassRate,
        lineChartLabels,
        chartDates,
        measuredChartLabels,
      ),
    [chartDates, lineChartLabels, measuredChartLabels, metricsQuery.data?.series.filteredPassRate],
  );
  const frequencyMiniChartData = useMemo(
    () =>
      buildMiniMetricLineChartSeries(
        metricsQuery.data?.series.executionTrendMetrics,
        lineChartLabels,
        chartDates,
        measuredChartLabels,
      ),
    [
      chartDates,
      lineChartLabels,
      measuredChartLabels,
      metricsQuery.data?.series.executionTrendMetrics,
    ],
  );
  const durationMiniChartData = useMemo(
    () =>
      buildMiniMetricLineChartSeries(
        metricsQuery.data?.series.executionDurationAverage,
        lineChartLabels,
        chartDates,
        measuredChartLabels,
      ),
    [
      chartDates,
      lineChartLabels,
      measuredChartLabels,
      metricsQuery.data?.series.executionDurationAverage,
    ],
  );
  const flakeScoreMiniChartData = useMemo(
    () =>
      buildMiniMetricLineChartSeries(
        metricsQuery.data?.series.flakeScoreTrend,
        lineChartLabels,
        chartDates,
        measuredChartLabels,
      ),
    [chartDates, lineChartLabels, measuredChartLabels, metricsQuery.data?.series.flakeScoreTrend],
  );
  const showStatusChartSkeleton = metricsQuery.isFetching && !metricsQuery.isError;
  const showChartErrorState = metricsQuery.isError;
  const passRateSummary = useMemo(
    () =>
      buildMiniMetricChartSummary({
        key: 'passRate',
        metrics: summaryMetrics,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: metricThresholds,
        showDelta: showMetricDelta,
      }),
    [metricThresholds, previousMetricsQuery.data, showMetricDelta, summaryMetrics],
  );
  const frequencySummary = useMemo(
    () =>
      buildMiniMetricChartSummary({
        key: 'frequency',
        metrics: summaryMetrics,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: metricThresholds,
        showDelta: showMetricDelta,
      }),
    [metricThresholds, previousMetricsQuery.data, showMetricDelta, summaryMetrics],
  );
  const flakeScoreSummary = useMemo(
    () =>
      buildMiniMetricChartSummary({
        key: 'flakeScore',
        metrics: summaryMetrics,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: metricThresholds,
        showDelta: showMetricDelta,
      }),
    [metricThresholds, previousMetricsQuery.data, showMetricDelta, summaryMetrics],
  );
  const durationSummary = useMemo(
    () =>
      buildMiniMetricChartSummary({
        key: 'avgDuration',
        metrics: summaryMetrics,
        previousMetrics: showMetricDelta ? (previousMetricsQuery.data ?? null) : null,
        thresholds: metricThresholds,
        showDelta: showMetricDelta,
      }),
    [metricThresholds, previousMetricsQuery.data, showMetricDelta, summaryMetrics],
  );

  return {
    toolbarState,
    statusChartData,
    passRateChartData,
    frequencyChartData,
    flakeScoreChartData,
    durationChartData,
    passRateMiniChartData,
    frequencyMiniChartData,
    flakeScoreMiniChartData,
    durationMiniChartData,
    showStatusChartSkeleton,
    showChartErrorState,
    passRateSummary,
    frequencySummary,
    flakeScoreSummary,
    durationSummary,
    facetCounts: {
      statusCounts: statusFacetCounts,
      entityCounts: entityFacetCounts,
    },
  };
};
